import React from 'react';

export interface CsEdge {
    fromId: string;
    toId: string;
    state?: 'idle' | 'active' | 'completed' | 'failed';
}

export interface CsNodePosition {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
}

interface CsConnectionLinesProps {
    edges: CsEdge[];
    nodes: CsNodePosition[];
}

/**
 * Dotted Bezier curves between canvas nodes — matches the
 * IntegrationConnectionLines style (dashed stroke + endpoint circles,
 * no arrowhead markers). Renders inside the canvas transform wrapper.
 *
 * States:
 *   idle      → subtle grey dashed, slow flow animation
 *   active    → dark dashed with fast flow animation
 *   completed → green dashed, no animation
 *   failed    → red dashed, no animation
 */
export const CsConnectionLines: React.FC<CsConnectionLinesProps> = ({ edges, nodes }) => {
    const nodeMap = new Map(nodes.map((n) => [n.id, n]));

    const maxX = Math.max(...nodes.map((n) => n.x + n.width), 0) + 800;
    const maxY = Math.max(...nodes.map((n) => n.y + n.height), 0) + 800;

    return (
        <svg
            width={Math.max(maxX, 2000)}
            height={Math.max(maxY, 2000)}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                pointerEvents: 'none',
                overflow: 'visible',
            }}
        >
            {edges.map((edge, i) => {
                const from = nodeMap.get(edge.fromId);
                const to = nodeMap.get(edge.toId);
                if (!from || !to) return null;

                // Connection points: right edge of source → left edge of target
                const fromX = from.x + from.width;
                const fromY = from.y + from.height / 2;
                const toX = to.x;
                const toY = to.y + to.height / 2;

                // Cubic bezier with horizontal control points
                const dx = Math.abs(toX - fromX);
                const tension = Math.max(40, dx * 0.4);
                const path = `M ${fromX} ${fromY} C ${fromX + tension} ${fromY}, ${toX - tension} ${toY}, ${toX} ${toY}`;

                const state = edge.state || 'idle';

                // CSS variables, not classes: SVG stroke/fill attributes cannot
                // read Tailwind utilities, and the vars follow the theme.
                const color =
                    state === 'completed' ? 'var(--success)'
                    : state === 'active' ? 'var(--foreground)'
                    : state === 'failed' ? 'var(--destructive)'
                    : 'var(--muted-foreground)';

                const opacity =
                    state === 'idle' ? 0.35
                    : state === 'active' ? 0.6
                    : 0.5;

                const animated = state === 'active' || state === 'idle';

                return (
                    <g key={`${edge.fromId}-${edge.toId}-${i}`}>
                        {/* Dashed path */}
                        <path
                            d={path}
                            fill="none"
                            stroke={color}
                            strokeWidth={1.5}
                            strokeDasharray="5,5"
                            strokeLinecap="round"
                            opacity={opacity}
                            style={animated ? {
                                animation: `csFlowDash ${state === 'active' ? '0.8s' : '3s'} linear infinite`,
                                animationDelay: `${i * 0.4}s`,
                            } : undefined}
                        />
                        {/* Endpoint dots */}
                        <circle
                            cx={fromX}
                            cy={fromY}
                            r={3.5}
                            fill={color}
                            opacity={opacity}
                        />
                        <circle
                            cx={toX}
                            cy={toY}
                            r={3.5}
                            fill={color}
                            opacity={state === 'active' ? opacity : opacity * 0.7}
                            style={state === 'active' ? {
                                animation: 'csDotPulse 1.2s ease-in-out infinite',
                            } : undefined}
                        />
                    </g>
                );
            })}
            <style>{`
                @keyframes csFlowDash {
                    to { stroke-dashoffset: -20; }
                }
                @keyframes csDotPulse {
                    0%, 100% { r: 3.5; opacity: 0.6; }
                    50% { r: 5; opacity: 0.9; }
                }
            `}</style>
        </svg>
    );
};
